import React, { useId } from 'react';
import { getTpmsTireColor, getTpmsTireTextColor, TireStatus } from 'utils/tpms';
import BatteryIndicator from 'components/Tire/BatteryIndicator';

interface CombinedFourSixWheelsProps {
  pressureValues: Record<string, string | null>;
  temperatureValues: Record<string, string | null>;
  pressureStatusMap: Record<string, TireStatus>;
  temperatureStatusMap: Record<string, TireStatus>;
  batteryLow?: Record<string, boolean>;
  tireStaleMap?: Record<string, boolean>;
  width?: number | string;
  height?: number | string;
}

// 4x6 reach-stacker: dual front axle (4 tires) + single rear axle (2 tires).
// Reversed layout of CombinedSixWheels.tsx ('6x4'): the quad axle sits at the
// top (front) and the single axle sits at the bottom (rear).
// viewBox: 0 0 160 200
// Front (quad) tires: width=22, height=56, half=28
// Rear (single) tires: width=24, height=56, half=28
//
// Layout:
//   p1 (Left-Front-Outside):  x=0,   y=1
//   p2 (Left-Front-Inside):   x=23,  y=1
//   p3 (Right-Front-Inside):  x=115, y=1
//   p4 (Right-Front-Outside): x=138, y=1
//   p5 (Left-Back):           x=12,  y=120
//   p6 (Right-Back):          x=124, y=120
//
// Front (quad) axle: y=29, connects inner tires (p2 to p3)
// Rear (single) axle: y=148, connects p5 to p6
// Spine: x=80, y=29 to y=148

const FRONT_W = 22;
const REAR_W = 24;
const TIRE_H = 56;
const TIRE_HALF = 28;

const FRONT_AXLE_Y = 29;
const REAR_AXLE_Y = 148;
const SPINE_X = 80;

const tireConfig: Array<{
  id: string;
  x: number;
  y: number;
  w: number;
}> = [
  { id: 'p1', x: 0, y: 1, w: FRONT_W },
  { id: 'p2', x: 23, y: 1, w: FRONT_W },
  { id: 'p3', x: 115, y: 1, w: FRONT_W },
  { id: 'p4', x: 138, y: 1, w: FRONT_W },
  { id: 'p5', x: 12, y: 120, w: REAR_W },
  { id: 'p6', x: 124, y: 120, w: REAR_W },
];

const CombinedFourSixWheels: React.FC<CombinedFourSixWheelsProps> = ({
  pressureValues,
  temperatureValues,
  pressureStatusMap,
  temperatureStatusMap,
  batteryLow,
  tireStaleMap,
  width = 170,
  height = 213,
}) => {
  const uid = useId().replace(/:/g, '');

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 160 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {tireConfig.map(({ id, x, y, w }) => (
          <clipPath key={id} id={`clip-${uid}-${id}`}>
            <rect x={x} y={y} width={w} height={TIRE_H} rx={3} />
          </clipPath>
        ))}
      </defs>

      {/* Front (quad) axle: connects inner tires */}
      <line
        x1={23 + FRONT_W}
        y1={FRONT_AXLE_Y}
        x2={115}
        y2={FRONT_AXLE_Y}
        stroke="#4A5C72"
        strokeWidth="6"
      />
      {/* Rear (single) axle */}
      <line
        x1={12 + REAR_W}
        y1={REAR_AXLE_Y}
        x2={124}
        y2={REAR_AXLE_Y}
        stroke="#4A5C72"
        strokeWidth="6"
      />
      {/* Center spine */}
      <line
        x1={SPINE_X}
        y1={FRONT_AXLE_Y}
        x2={SPINE_X}
        y2={REAR_AXLE_Y}
        stroke="#4A5C72"
        strokeWidth="6"
      />

      {tireConfig.map(({ id, x, y, w }) => {
        const pKey = id;
        const pressureColor = getTpmsTireColor(
          pressureStatusMap[pKey] ?? TireStatus.NO_SENSOR,
          tireStaleMap?.[pKey]
        );
        const tempColor = getTpmsTireColor(
          temperatureStatusMap[pKey] ?? TireStatus.NO_SENSOR,
          tireStaleMap?.[pKey]
        );
        const pressureTextColor = getTpmsTireTextColor(
          pressureStatusMap[pKey] ?? TireStatus.NO_SENSOR,
          tireStaleMap?.[pKey]
        );
        const tempTextColor = getTpmsTireTextColor(
          temperatureStatusMap[pKey] ?? TireStatus.NO_SENSOR,
          tireStaleMap?.[pKey]
        );
        const pVal = pressureValues[pKey] ?? '--';
        const tVal = temperatureValues[pKey] ?? '--';
        const cx = x + w / 2;
        const pyCenter = y + TIRE_HALF / 2;
        const tyCenter = y + TIRE_HALF + TIRE_HALF / 2;

        return (
          <g key={id}>
            <g clipPath={`url(#clip-${uid}-${id})`}>
              <rect
                x={x}
                y={y}
                width={w}
                height={TIRE_HALF}
                fill={pressureColor}
              />
              <rect
                x={x}
                y={y + TIRE_HALF}
                width={w}
                height={TIRE_HALF}
                fill={tempColor}
              />
            </g>
            <line
              x1={x}
              y1={y + TIRE_HALF}
              x2={x + w}
              y2={y + TIRE_HALF}
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="0.5"
            />
            <text
              x={cx}
              y={pyCenter}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={pressureTextColor}
              fontSize={8}
              fontWeight={600}
              style={{ pointerEvents: 'none' }}
            >
              {pVal}
            </text>
            <text
              x={cx}
              y={tyCenter}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={tempTextColor}
              fontSize={8}
              fontWeight={600}
              style={{ pointerEvents: 'none' }}
            >
              {tVal}
            </text>
            <BatteryIndicator
              cx={cx}
              cy={y}
              tireBottomY={y + TIRE_H}
              tireWidth={w}
              show={batteryLow?.[pKey]}
              fullCover
            />
          </g>
        );
      })}
    </svg>
  );
};

export default CombinedFourSixWheels;
